<template>
    <div class="ah-page accounting-annual-tax">
        <div class="page-header bottom-margin">
            <div class="ah-container">
                <div class="title-row">
                    <div class="title"><h1>年度扣繳申報</h1></div>
                    <div class="actions">
                        <el-button type="normal" @click="onExportSelected">匯出 Sokuji 資料 Excel 檔</el-button>
                        <el-button type="normal" @click="onExportAnnualDeclareExcel">匯出申報 Excel 檔</el-button>
                    </div>
                </div>
            </div>
        </div>
        <div class="ah-container">
            <div class="ah-panel">
                <div class="panel-body">
                    <div class="tool-block">
                        <div style="display: flex; gap: 10px">
                            <el-select v-model="year" placeholder="年度" size="small" @change="onYearChange">
                                <el-option v-for="y in yearList" :key="y" :label="`${y} 年度`" :value="y" />
                            </el-select>
                            <el-input v-model="keyword" size="small" style="width: 250px" placeholder="關鍵字：姓名、身分證號碼" @keyup.enter.native="fetchData">
                                <i slot="suffix" class="el-input__icon el-icon-search" style="cursor: pointer" @click="fetchData" />
                            </el-input>
                        </div>
                    </div>
                </div>

                <div class="hint gray small">
                    <div>
                        下列資料為「已完成的請款單」付款對象為「委外勞務」的資料列表。
                    </div>
                    <el-table
                        :data="datas"
                        ref="table"
                        empty-text="無資料"
                        style="width: 100%"
                        show-summary
                        :summary-method="getTableSummaries"
                        @selection-change="onSelectChange"
                        v-loading="isLoading"
                    >
                        <el-table-column key="selection" type="selection" width="50"></el-table-column>
                        <el-table-column type="expand" width="58">
                            <template slot-scope="scope">
                                <div class="detail-block">
                                    <el-tabs v-model="activeDetailTypeTab" type="border-card">
                                        <el-tab-pane name="payment">
                                            <span slot="label">請款單 ({{ scope.row.payments.length }})</span>
                                            <PaymentSubTable :data="scope.row.payments" @open="onPaymentDetailDialogOpen" />
                                        </el-tab-pane>
                                        <el-tab-pane name="service" v-if="isPayed('H01')">
                                            <span slot="label">勞報單 ({{ scope.row.serviceFeeStatementItems.length }})</span>
                                            <ServiceFeeStatementSubTable
                                                :data="scope.row.serviceFeeStatementItems"
                                                @open="onServiceFeeStatementDetailDialogOpen"
                                                @openPayment="onPaymentDetailDialogOpen"
                                            />
                                        </el-tab-pane>
                                    </el-tabs>
                                </div>
                            </template>
                        </el-table-column>
                        <el-table-column prop="nam" label="姓名" width="80"></el-table-column>
                        <el-table-column prop="bsn" label="身分證/統編" width="130">
                            <template slot-scope="scope">
                                <div>{{ protectedNumber(scope.row.bsn) }}</div>
                            </template>
                        </el-table-column>
                        <el-table-column prop="adr" label="地址" :show-overflow-tooltip="true" min-width="150">
                            <template slot-scope="scope"> {{ scope.row.cty }}{{ scope.row.adr }} </template>
                        </el-table-column>
                        <el-table-column prop="serviceType" label="所得類型" width="80">
                            <template slot-scope="scope">
                                {{ getIncomeTypeTxt(scope.row.serviceType) }}
                            </template>
                        </el-table-column>
                        <el-table-column prop="serviceSubType" label="業別代號" width="130" show-overflow-tooltip>
                            <template slot-scope="scope">
                                {{ getServiceTypeTxt(scope.row.serviceSubType) }}
                            </template>
                        </el-table-column>
                        <el-table-column prop="amount" align="right" width="130">
                            <template slot="header"> 給付所得總額<ah-tip>該年度已完成的請款總額</ah-tip> </template>
                            <template slot-scope="scope">
                                <Money :v="scope.row.amount" zero="-" />
                            </template>
                        </el-table-column>
                        <el-table-column prop="taX_AMT" label="稅款" align="right" width="100">
                            <template slot-scope="scope">
                                <Money :v="scope.row.taX_AMT" zero="-" />
                            </template>
                        </el-table-column>
                        <el-table-column prop="incomeNet" label="所得淨額" align="right" width="120">
                            <template slot="header"> 所得淨額<ah-tip>業務執行所得 - 稅款</ah-tip> </template>
                            <template slot-scope="scope">
                                <Money :v="scope.row.incomeNet" zero="-" />
                            </template>
                        </el-table-column>
                        <el-table-column prop="geN2_AMT" label="代扣二代健保" align="right" width="120">
                            <template slot-scope="scope">
                                <Money :v="scope.row.geN2_AMT" zero="-" />
                            </template>
                        </el-table-column>
                        <el-table-column prop="unPayAmount" label="單人匯出" align="center" width="120">
                            <template slot-scope="scope">
                                <el-button type="primary" plain size="small" @click="onExportSingle(scope.row)"> 單人匯出 </el-button>
                            </template>
                        </el-table-column>
                    </el-table>
                </div>
            </div>
        </div>
        <PaymentDetailDialog :visible="paymentDetailDialog.visible" :sno="paymentDetailDialog.sno" @close="onPaymentDetailDialogClose" />
        <ServiceFeeStatementDetailDialog :visible="serviceFeeStatementDetailDialog.visible" :sno="serviceFeeStatementDetailDialog.sno" @close="onServiceFeeStatementDetailDialogClose" />
    </div>
</template>
<script>
import _ from 'lodash';
import moment from 'moment';
import { serviceTypeList, serviceType9AList, serviceType9BList } from '@/assets/commonData/options';
import ahTip from '@/components/ui/ahTip.vue';
import PaymentDetailDialog from '@/components/dashboard/Payment/PaymentDetailDialog.vue';
import PaymentStatusLabel from '@/components/dashboard/Payment/PaymentStatusLabel.vue';
import ServiceFeeStatementDetailDialog from '@/components/dashboard/ServiceFeeStatement/DetailDialog.vue';
import PaymentSubTable from './AnnualTaxPaymentSubTable.vue';
import ServiceFeeStatementSubTable from './AnnualTaxServiceFeeStatementSubTable.vue';
import ExcelMixins from './ExcelMixins';

export default {
    mixins: [ExcelMixins],
    components: {
        ahTip,
        PaymentSubTable,
        PaymentStatusLabel,
        ServiceFeeStatementSubTable,
        PaymentDetailDialog,
        ServiceFeeStatementDetailDialog,
    },
    data() {
        return {
            isLoading: false,
            keyword: undefined,
            year: null,
            yearList: [],
            selectedItems: [],
            timespan: null,
            queryParams: {
                StartDate: null,
                EndDate: null,
                Keyword: undefined,
            },
            datas: [],

            checkAll: false,
            showSummary: true,
            detailDisplayMode: 'tabs', // 明細顯示模式：tabs | list
            activeDetailTypeTab: 'payment', // 當前顯示明細分頁 payment | service

            paymentDetailDialog: {
                visible: false,
                sno: null,
            },
            serviceFeeStatementDetailDialog: {
                visible: false,
                sno: null,
            },
        };
    },
    mounted() {
        this.init();
    },
    methods: {
        init() {
            const THIS_YEAR = moment().year();
            for (let i = THIS_YEAR; i >= 2020; i -= 1) {
                this.yearList.push(i);
            }
            const urlYear = Number.parseInt(this.$route.query.year, 10);
            if (urlYear) {
                this.year = urlYear;
            } else {
                this.year = THIS_YEAR;
            }
            this.fetchData();
        },
        async fetchData() {
            this.isLoading = true;
            this.checkAll = false;

            const DATE_OF_THIS_YEAR = moment().year(this.year);
            this.queryParams.StartDate = DATE_OF_THIS_YEAR.startOf('year').format('x');
            this.queryParams.EndDate = DATE_OF_THIS_YEAR.endOf('year').format('x');
            this.queryParams.Keyword = this.keyword || undefined;

            try {
                this.isLoading = true;
                const resp = await this.$api({
                    url: 'AccountingSummaryPage/QueryWithholdingTax',
                    params: this.queryParams,
                    method: 'GET',
                });

                console.log('annoual tax', resp.data);

                this.datas = _.map(resp.data.items, (o) => ({ ...o, checked: false, isIndeterminate: false }));
            } finally {
                this.isLoading = false;
            }
        },

        // Table Layout
        getTableSummaries(param) {
            const detaisCheckedList = this.selectedItems;
            const isAnyItemSelected = this.selectedItems.length > 0;
            const { columns, data } = param;
            const sums = [];
            const SUM_TARGET = isAnyItemSelected ? detaisCheckedList : data;

            const totalAmount = _.sumBy(SUM_TARGET, 'amount');
            const totalTax = _.sumBy(SUM_TARGET, 'taX_AMT');
            const totalGen2 = _.sumBy(SUM_TARGET, 'geN2_AMT');
            const totalIncomeNet = _.sumBy(SUM_TARGET, 'incomeNet');

            columns.forEach((column, index) => {
                if (column.property === 'nam') {
                    sums[index] = `${isAnyItemSelected ? '(已勾選)' : ''} 總計`;
                    return;
                }
                if (column.property === 'amount') {
                    sums[index] = totalAmount !== 0 ? Number(totalAmount).toLocaleString('en') : '-';
                    return;
                }
                if (column.property === 'taX_AMT') {
                    sums[index] = totalTax !== 0 ? Number(totalTax).toLocaleString('en') : '-';
                    return;
                }
                if (column.property === 'geN2_AMT') {
                    sums[index] = totalGen2 !== 0 ? Number(totalGen2).toLocaleString('en') : '-';
                    return;
                }
                if (column.property === 'incomeNet') {
                    sums[index] = totalIncomeNet !== 0 ? Number(totalIncomeNet).toLocaleString('en') : '-';
                }
            });

            return sums;
        },

        getServiceTypeDescribe(serviceTypeNum) {
            const s = serviceTypeList.find((x) => x.value === serviceTypeNum);
            if (s) return s.label;
            return '';
        },
        getIncomeTypeTxt(serviceTypeNum) {
            const s = serviceTypeList.find((x) => x.value === serviceTypeNum);
            if (s) return s.type;
            return '';
        },
        getServiceTypeTxt(serviceSubType) {
            const s = _.find([...serviceType9AList, ...serviceType9BList], ['key', serviceSubType]);
            return s ? `${s.key}：${s.label}` : '無';
        },
        getServiceTypeHealthInsuranceCode(serviceTypeNum) {
            const s = serviceTypeList.find((x) => x.value === serviceTypeNum);
            if (s) return s.healthInsuranceCode;
            return '';
        },

        // 個資保護
        protectedNumber(text) {
            return this.protectedInfo(text, 'cover', 4, true);
        },

        onSelectChange(selected) {
            this.selectedItems = selected;
            // this.selected
            for (let i = 0; i < this.datas.length; i += 1) {
                if (selected.indexOf(this.datas[i]) !== -1) {
                    this.datas[i].checked = true;
                } else {
                    this.datas[i].checked = false;
                }
            }
        },

        // 選擇年度
        onYearChange() {
            this.$router.replace({ name: 'accounting-annual-tax', query: { year: this.year } });
            this.fetchData();
        },

        // 請款單 Popup action
        onPaymentDetailDialogOpen(sno) {
            this.paymentDetailDialog.visible = true;
            this.paymentDetailDialog.sno = sno;
        },
        onPaymentDetailDialogClose() {
            this.paymentDetailDialog.visible = false;
            this.paymentDetailDialog.sno = null;
        },

        // 勞報單 Popup action
        onServiceFeeStatementDetailDialogOpen(sno) {
            this.serviceFeeStatementDetailDialog.visible = true;
            this.serviceFeeStatementDetailDialog.sno = sno;
        },
        onServiceFeeStatementDetailDialogClose() {
            this.serviceFeeStatementDetailDialog.visible = false;
            this.serviceFeeStatementDetailDialog.sno = null;
        },
    },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/vars.scss";

.accounting-annual-tax {
    .tool-block {
        display: flex;
        justify-content: space-between;
        align-items: center;
        .form {
            display: flex;
            > :not(:last-child) {
                margin-right: 20px;
            }
        }
    }

    .hint {
        position: relative;
        // background-color: rgb(199 199 199 / 10%);
        margin: 0;
        padding: 10px 20px;
        color: #aaa;
        font-size: 9.5pt;
        border-top-left-radius: 15px;
        border-top-right-radius: 15px;

        strong {
            color: #666;
        }
    }
}
</style>

<style lang="scss">
.accounting-annual-tax {
    .el-form-item__label {
        line-height: 26px;
    }
    .el-table__footer-wrapper tbody td {
        font-weight: bold;
    }
    .el-table__expanded-cell {
        padding-left: 0;
        padding-right: 0;
    }
    .el-table__expand-icon {
        background-color: rgba(0, 0, 0, 0.05);
        width: 25px;
        height: 25px;
        border-radius: 5px;

        &:hover {
            background-color: rgba(0, 0, 0, 0.08);
        }
    }

    .detail-block {
        // background-color: #eee;
        border-radius: 8px;
        margin: 0 0 20px 100px;

        > .table-title-block {
            font-size: 12pt;
            font-weight: bold;
            padding: 0 0 5px 10px;
            margin: 0 0 0 0;
            border-bottom: 2px solid #463cb2;
            color: #463cb2;
        }
        .el-table__header-wrapper tbody td,
        .el-table__footer-wrapper tbody td {
            background-color: transparent;
        }
    }
}
</style>
